import React from 'react'
import { Typography, Link, Box, Theme } from '@mui/material'
import type { Components } from 'react-markdown'
import { getImageSizes, getPureAltText } from '@e3dc-react/shell/Elements/MarkdownEditor'

type GetReactMarkdownComponentsType = (theme: Theme) => Components

/**
 * getReactMarkdownComponents function that is used for the react-markdown Component
 * It maps markdown syntax to our components instead of normal html tags
 * At the Moment headings, blockquote, links, lists, images, and tables are included
 */
export const getReactMarkdownComponents: GetReactMarkdownComponentsType = theme => ({
  h1(props) {
    const { node, ...rest } = props
    return <Typography variant="h1" {...rest} />
  },
  h2(props) {
    const { node, ...rest } = props
    return <Typography variant="h2" {...rest} />
  },
  blockquote(props) {
    const { node, ...rest } = props

    return (
      <Box
        sx={{
          borderLeftColor: theme.palette.filter.activeText,
          borderLeftWidth: '4px',
          borderLeftStyle: 'solid',
          paddingLeft: 3,
          marginLeft: 2,
        }}
      >
        <Typography variant="h2" {...rest} />
      </Box>
    )
  },
  h3(props) {
    const { node, ...rest } = props
    return <Typography variant="h3" {...rest} />
  },
  h4(props) {
    const { node, ...rest } = props
    return <Typography variant="h4" {...rest} />
  },
  h5(props) {
    const { node, ...rest } = props
    return <Typography variant="h5" {...rest} />
  },
  h6(props) {
    const { node, ...rest } = props
    return <Typography variant="h6" {...rest} />
  },
  a(props) {
    const { node, ...rest } = props
    return <Link {...rest} target="_blank" />
  },
  p(props) {
    const { node, ...rest } = props
    return <Typography variant="body2" {...rest} />
  },
  ul(props) {
    const { node, ordered, ...rest } = props
    return (
      <Box
        sx={{
          '.contains-task-list': {
            paddingLeft: 0,
          },
          ul: {
            '& p': {
              display: 'inline',
            },
            paddingLeft: 0,
            listStylePosition: 'inside',
            margin: 0,
            li: {
              fontSize: theme.shape.markdownEditor.defaultFontSize,
              lineHeight: theme.typography.body2.lineHeight,
            },
            ul: {
              paddingLeft: 2,
            },
            '.task-list-item': {
              '& input': {
                marginLeft: 0,
              },
              listStyleType: 'none',
              '.task-list-item': {
                paddingLeft: 2,
              },
            },
          },
        }}
      >
        <ul {...rest} />
      </Box>
    )
  },
  ol(props) {
    const { node, ordered, ...rest } = props
    return (
      <Box
        sx={{
          ol: {
            '& p': {
              display: 'inline',
            },
            paddingLeft: 0,
            listStylePosition: 'inside',
            margin: 0,
            li: {
              fontSize: theme.shape.markdownEditor.defaultFontSize,
              lineHeight: theme.typography.body2.lineHeight,
            },
            ol: {
              paddingLeft: 2,
              listStyleType: 'upper-alpha',
              ol: {
                listStyleType: 'lower-alpha',
                ol: {
                  listStyleType: 'upper-roman',
                  ol: {
                    listStyleType: 'lower-roman',
                    ol: {
                      listStyleType: 'decimal',
                    },
                  },
                },
              },
            },
          },
        }}
      >
        <ol {...rest} />
      </Box>
    )
  },
  img(props) {
    const alt = (props.alt || '') as string
    const altText = getPureAltText(alt)
    const { height, width } = getImageSizes(alt)

    return (
      <Box
        component="img"
        sx={{
          maxWidth: '100%',
          maxHeight: '3000px',
          width: width ? `${width}px` : undefined,
          height: height ? `${height}px` : undefined,
        }}
        src={props?.src as string}
        alt={altText}
      />
    )
  },
  table(props) {
    const { node, ...rest } = props

    return (
      <Box
        sx={{
          width: '100%',
          overflowX: 'auto',
          table: {
            width: '100%',
            borderSpacing: 0,
            td: {
              border: `1px solid ${theme.palette.buttonGroup.borderColor}`,
              borderRight: `none`,
              borderTop: 'none',
              padding: theme.spacing(1),
              textAlign: 'center',
              overflowWrap: 'break-word',
              ':last-of-type': {
                borderRight: `1px solid ${theme.palette.buttonGroup.borderColor}`,
              },
            },
            th: {
              border: `1px solid ${theme.palette.buttonGroup.borderColor}`,
              borderRight: 'none',
              padding: theme.spacing(1),
              fontWeight: theme.shape.markdownEditor.defaultFontWeight,
              overflowWrap: 'break-word',
              background: theme.shape.markdownEditor.tableHeaderBackground,
              ':last-of-type': {
                borderRight: `1px solid ${theme.palette.buttonGroup.borderColor}`,
              },
            },
          },
        }}
      >
        <table {...rest} />
      </Box>
    )
  },
  th(props) {
    const { node, children, isHeader, ...rest } = props
    const start = props.node.position?.start
    const end = props.node.position?.end

    return (
      <th {...rest} key={`${start}-${end}`}>
        {children || <br />}
      </th>
    )
  },
  td(props) {
    const { node, children, isHeader, ...rest } = props
    const start = props.node.position?.start
    const end = props.node.position?.end

    return (
      <td {...rest} key={`${start}-${end}`}>
        {children || <br />}
      </td>
    )
  },
})
